import { Agente } from "../dto/Agentes";

export async function validarCadastroAgente(agente: Agente): Promise<{ [key: string]: string }> {
  const erros: { [key: string]: string } = {};

  // Limpar valores antes da validação
  agente.cnpjCpf =
    agente.pessoaJuridica === "S"
      ? normalizarCNPJ(agente.cnpjCpf || "")
      : limparNumeros(agente.cnpjCpf || "");
  agente.cep = limparNumeros(agente.cep);
  agente.fone1 = limparNumeros(agente.fone1);

  // Validações de campos obrigatórios
  if (!agente.cnpjCpf) {
    erros["cnpjCpf"] = "Documento é obrigatório";
  } else if (agente.pessoaJuridica === "S" && agente.cnpjCpf.length !== 14) {
    erros["cnpjCpf"] = "CNPJ deve conter 14 caracteres alfanuméricos";
  } else if (agente.pessoaJuridica === "N" && agente.cnpjCpf.length !== 11) {
    erros["cnpjCpf"] = "CPF deve conter 11 dígitos";
  } else if (
    agente.pessoaJuridica !== "S" &&
    agente.pessoaJuridica !== "N" &&
    agente.cnpjCpf.length !== 11 &&
    agente.cnpjCpf.length !== 14
  ) {
    erros["cnpjCpf"] = "Documento deve conter 11 ou 14 caracteres";
  }

  if (!agente.nome) erros["nome"] = "Razão Social é obrigatória";
  if (!agente.idEmpresa) erros["idEmpresa"] = "O campo 'idEmpresa' é obrigatório";

  if (!agente.logradouro) erros["logradouro"] = "Logradouro é obrigatório";
  if (!agente.numero) erros["numero"] = "Número é obrigatório";
  if (!agente.bairro) erros["bairro"] = "Bairro é obrigatório";

  if (!agente.idCidade) {
    erros["idUf"] = "UF é obrigatória";
    erros["idCidade"] = "Cidade é obrigatória";
  }

  // Validações de formato
  if (agente.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(agente.email)) {
    erros["email"] = "O campo 'email' está em formato inválido";
  }

  if (agente.cep && !/^\d{5}-?\d{3}$/.test(agente.cep.replace(/[^\d-]/g, ''))) {
    erros["cep"] = "O campo 'cep' está em formato inválido";
  }

  return erros;
}

export function limparNumeros(valor: any) {
  return valor ? valor.replace(/\D/g, '') : valor;
}

export function normalizarTextoAscii(valor: string): string {
  if (!valor) return "";
  return valor
    .normalize("NFKC")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .trim()
    .toUpperCase();
}

export function normalizarCNPJ(valor: string): string {
  return normalizarTextoAscii(valor).replace(/[./-\s]/g, "");
}

function caractereParaValorCNPJ(caractere: string): number {
  return caractere.charCodeAt(0) - 48;
}

function calcularDigitoCNPJ(base: string, pesos: number[]): number {
  const soma = base
    .split("")
    .reduce((acc, caractere, index) => acc + caractereParaValorCNPJ(caractere) * pesos[index], 0);

  const resto = soma % 11;
  return resto === 0 || resto === 1 ? 0 : 11 - resto;
}

export function calcularDigitosCNPJ(base12: string): string | null {
  const baseNormalizada = normalizarCNPJ(base12);
  if (!/^[0-9A-Z]{12}$/.test(baseNormalizada)) return null;

  const pesosDv1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const pesosDv2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

  const dv1 = calcularDigitoCNPJ(baseNormalizada, pesosDv1);
  const dv2 = calcularDigitoCNPJ(`${baseNormalizada}${dv1}`, pesosDv2);

  return `${dv1}${dv2}`;
}

export function formataFiltrosBusca(pCampo: string | null, pValor: string): string {
  if (pCampo === 'total') {
    pValor = pValor.replace(',', '.');
    pValor = pValor.replace(/\.0+$/, '');
  }

  if (pCampo === 'cnpjCpf') {
    const cpf = limparNumeros(pValor);
    if (cpf.length === 11) return cpf;

    const cnpj = normalizarCNPJ(pValor);
    if (cnpj.length === 14) return cnpj;
    return normalizarTextoAscii(pValor);
  }

  // Limpa todos os caracteres não numéricos para os demais campos
  const valorNumerico = pValor.replace(/\D/g, '');
  if (valorNumerico.length > 0) {
    pValor = valorNumerico;
  }

  return pValor;
}

export function validarCPF(cpf: any) {
  cpf = limparNumeros(cpf);
  if (!cpf || cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;

  let soma = 0, resto;
  for (let i = 1; i <= 9; i++) soma += parseInt(cpf[i - 1]) * (11 - i);
  resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  if (resto !== parseInt(cpf[9])) return false;

  soma = 0;
  for (let i = 1; i <= 10; i++) soma += parseInt(cpf[i - 1]) * (12 - i);
  resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  return resto === parseInt(cpf[10]);
}

export function validarCNPJ(cnpj: any) {
  const cnpjNormalizado = normalizarCNPJ(cnpj || "");

  // CNPJ completo: 12 posições alfanuméricas e 2 DVs numéricos
  if (!/^[0-9A-Z]{12}\d{2}$/.test(cnpjNormalizado)) return false;

  // Mantém comportamento do CNPJ numérico clássico inválido por repetição
  if (/^(\d)\1{13}$/.test(cnpjNormalizado)) return false;

  const base = cnpjNormalizado.substring(0, 12);
  const digitosInformados = cnpjNormalizado.substring(12);
  const digitosCalculados = calcularDigitosCNPJ(base);

  if (!digitosCalculados) return false;
  return digitosInformados === digitosCalculados;
}

export async function validarDocumento(
  documento: string,
  tipo: string
): Promise<string | null> {

  if (tipo === "S") {
    const valido = await validarCNPJ(documento);
    return valido ? null : "CNPJ inválido";
  }

  const cpf = limparNumeros(documento);
  if (tipo === "N") {
    const valido = await validarCPF(cpf);
    return valido ? null : "CPF inválido";
  }

  return null;
}

/* export function formatarErros(erros: string[]): { [key: string]: string } {
  return erros.reduce((acc, campo) => {
    const match = campo.match(/(.+?)$/);
    if (match) {
      const campo = match[1];
      acc[campo] = campo;
    }
    return acc;
  }, {} as { [key: string]: string });
} */

